import { useEffect, useMemo, useRef, useState } from "react";
import SearchableSelect from "../../BaseComponents/SearchableSelect/SearchableSelect";
import { getCities, getCityById } from "../../../api/geo/useCities";

// Server-side searchable city / destination picker.
//
// WHY THIS EXISTS: a plain <select> that pre-loads a fixed slice of cities (the Zones page
// loaded the first 500, alphabetically) only ever reaches the start of the alphabet — the
// client reported it "only comes until B". There are thousands of cities, so the list MUST be
// queried as you type rather than shipped up front. This wraps SearchableSelect (which already
// debounces an onSearch callback) and hits the cities API per keystroke.
//
// Props:
//   value      selected city id (string|number) — "" means none / All
//   onChange   (e) => void, receives a native-select-like event: e.target.value = city id
//   allowAll   prepend an "All Cities" option (for the filter use); omit inside a form
export default function DestinationSelect({
  value,
  onChange,
  placeholder = "Select city",
  searchPlaceholder = "Type a city name…",
  allowAll = false,
  allLabel = "All Cities",
  width,
  disabled = false,
  error = false,
}) {
  const [options, setOptions] = useState([]);
  const [label, setLabel] = useState("");   // resolved name for the current `value`
  const reqRef = useRef(0);

  // Query the API for a page of matching cities. Guarded by a request id so a slow earlier
  // response can't overwrite a newer one.
  const runSearch = (q) => {
    const id = ++reqRef.current;
    getCities({ page: 1, limit: 50, search: q?.trim() || undefined, sortBy: "name", sortOrder: "ASC" })
      .then((res) => {
        if (id !== reqRef.current) return;
        // A few source cities have no name; drop them so the picker never shows a blank option.
        setOptions(
          (res?.data || [])
            .filter((c) => c?.name && String(c.name).trim())
            .map((c) => ({ label: c.name, value: String(c.id) }))
        );
      })
      .catch(() => { if (id === reqRef.current) setOptions([]); });
  };

  // No initial fetch here on purpose: SearchableSelect fires onSearch("") once after mount (its
  // debounce runs on the empty query), which loads exactly this first page. Doing it here too
  // just asked the server for the same list twice on every mount.

  // Resolve the selected city's NAME so the trigger shows it even when that city isn't on the
  // current search page — e.g. the filter was set from a URL param, or the form is editing a
  // zone whose city is far down the alphabet.
  useEffect(() => {
    if (!value) { setLabel(""); return; }
    const inOpts = options.find((o) => o.value === String(value));
    if (inOpts) { setLabel(inOpts.label); return; }
    let live = true;
    getCityById(value)
      .then((res) => { const c = res?.data ?? res; if (live && c?.name) setLabel(c.name); })
      .catch(() => {});
    return () => { live = false; };
  }, [value, options]);

  const merged = useMemo(
    () => (allowAll ? [{ label: allLabel, value: "" }, ...options] : options),
    [allowAll, allLabel, options]
  );

  return (
    <SearchableSelect
      value={value ? String(value) : ""}
      onChange={(v) => onChange({ target: { value: v } })}   // shape it like a <select> event
      options={merged}
      onSearch={runSearch}
      initialLabel={label}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      width={width}
      disabled={disabled}
      error={error}
    />
  );
}
